// src/components/Cart.tsx - Separate Cart Component
import React from 'react';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { useAppNavigate } from '../hooks/useAppNavigate';
import { toggleCart } from '../redux/slices/cartSlice';
import { FaTrash, FaPlus, FaMinus, FaTimes, FaShoppingBag } from 'react-icons/fa';
import { 
  getCartItemName, 
  getCartItemImage, 
  formatModifiersForDisplayWithQuantity,
} from '../utils/cartUtils';
import { formatPrice } from '../utils/priceUtils';
import { useMenuItemActions } from '../hooks/useMenuItemActions';
import { ItemModifierModal } from './ItemModifierModal';
import { RepeatCustomisationModal, DeleteVariantModal } from './modifiers';

const Cart: React.FC = () => {
  const appNavigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const { items: cartItems, totalItems, totalAmount, isOpen } = useAppSelector(state => state.cart);

  // 🛒 Use centralized menu item actions hook
  const {
    isModifierModalOpen,
    isRepeatCustomisationModalOpen,
    isDeleteVariantModalOpen,
    currentMenuItem,
    selectedCartItem,
    editMode,
    deleteVariants,
    handleIncreaseQuantityClick,
    handleDecreaseQuantityClick,
    handleRepeatCustomisation,
    handleChooseNewCustomisation,
    handleAddToCartFromModal,
    handleUpdateCartItem,
    handleDeleteVariant,
    closeModifierModal,
    closeRepeatCustomisationModal,
    closeDeleteVariantModal,
  } = useMenuItemActions();

  const handleCartToggle = () => {
    dispatch(toggleCart());
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 transition-opacity"
        onClick={handleCartToggle}
      />
      
      {/* Cart Sidebar - Responsive */}
      <div className="absolute right-0 top-0 h-full w-full sm:max-w-full md:w-[70vw] lg:w-[420px] bg-white shadow-2xl transform transition-transform flex flex-col">
        <div className="flex flex-col h-full">
          {/* Cart Header with Safe Area */}
          <div className="sticky top-0 z-20 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <FaShoppingBag className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#FA2C37' }} />
              <h2 className="text-base sm:text-lg font-bold text-gray-900">Your Cart</h2>
              {totalItems > 0 && (
                <span className="text-white text-sm px-2 py-1 rounded-full" style={{ backgroundColor: '#FA2C37' }}>
                  {totalItems}
                </span>
              )}
            </div>
            <button
              onClick={handleCartToggle}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FaTimes className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Cart Content with iOS-safe scroll */}
          <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y">
            {cartItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                <FaShoppingBag className="w-16 h-16 text-gray-300 mb-4" />
                <h3 className="text-base font-semibold text-gray-900 mb-2">Your cart is empty</h3>
                <p className="text-sm text-gray-500 mb-6">Add some delicious items to get started!</p>
                <button
                  onClick={() => {
                    handleCartToggle();
                    appNavigate('/menu');
                  }}
                  className="text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                  style={{ backgroundColor: '#FA2C37' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E02429'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FA2C37'}
                >
                  Browse Menu
                </button>
              </div>
            ) : (
              <div className="p-4 space-y-4">
                {cartItems.map((item) => (
                  <div key={item.cartItemId} className="bg-white rounded-xl border border-gray-100 shadow-sm">
                    <div className="p-3 sm:p-4">
                      <div className="flex gap-3 sm:gap-4">
                        {/* Item Image - Responsive */}
                        <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden flex-shrink-0">
                          {getCartItemImage(item) ? (
                            <img
                              src={getCartItemImage(item)}
                              alt={getCartItemName(item)}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.currentTarget;
                                target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center" style={{ background: `linear-gradient(to bottom right, #FA2C37, #FA2C37)` }}>
                              <span className="text-2xl font-bold text-white">
                                {getCartItemName(item).charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Item Details */}
                        <div className="flex-1 min-w-0">
                          {/* Top Row: Product Name and Total Price */}
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-semibold text-sm sm:text-base text-gray-900 truncate">{getCartItemName(item)}</h4>
                            <span className="text-sm sm:text-base font-bold" style={{ color: '#FA2C37' }}>
                              {formatPrice(item.totalPrice * item.quantity)}
                            </span>
                          </div>
                          
                          {/* Second Row: Modifiers Display - Overflow-safe */}
                          {formatModifiersForDisplayWithQuantity(item.selected.modifiers, item.quantity).length > 0 && (
                            <div className="mb-2">
                              <div className="text-xs sm:text-sm text-gray-600 space-y-1">
                                {formatModifiersForDisplayWithQuantity(item.selected.modifiers, item.quantity).map((modifier: any, index: number) => (
                                  <div key={index} className="flex flex-wrap items-center justify-between gap-x-2">
                                    <span className="font-medium leading-snug">
                                      {modifier.options}
                                    </span>
                                    <span className="text-green-600">
                                      ({modifier.formattedPrice})
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Bottom Row: Quantity Controls - Touch-safe */}
                          <div className="flex items-center justify-end">
                            <div className="flex items-center gap-2">
                              <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                      <button
                        onClick={() => handleDecreaseQuantityClick(item)}
                                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-md bg-white flex items-center justify-center transition-all"
                                >
                                  <FaMinus className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                                </button>
                                <span className="w-8 sm:w-10 text-center font-bold text-sm sm:text-base text-gray-900">{item.quantity}</span>
                      <button
                        onClick={() => handleIncreaseQuantityClick(item)}
                                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-md bg-white flex items-center justify-center transition-all"
                                >
                                  <FaPlus className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                                </button>
                              </div>
                              
                    <button
                      onClick={() => handleDecreaseQuantityClick(item)}
                                className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-red-50 hover:bg-red-100 flex items-center justify-center transition-all group"
                              >
                                <FaTrash className="w-3 h-3 sm:w-4 sm:h-4 text-red-500 group-hover:text-red-600" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cart Footer with iOS Safe Area */}
          {cartItems.length > 0 && (
            <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 space-y-4 shadow-lg" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}>
              {/* Total */}
              <div className="flex items-center justify-between">
                <span className="text-base sm:text-lg font-bold text-gray-900">Total</span>
                <span className="text-base sm:text-lg font-bold" style={{ color: '#FA2C37' }}>{formatPrice(totalAmount)}</span>
              </div>

              {/* Action Buttons - Responsive */}
              <div className="space-y-3">
                <button
                  onClick={() => {
                    handleCartToggle();
                    appNavigate('/cart');
                  }}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900 font-semibold py-3 text-sm sm:text-base rounded-lg transition-colors"
                >
                  View Cart
                </button>
                <button
                  onClick={() => {
                    handleCartToggle();
                    appNavigate('/checkout');
                  }}
                  className="w-full text-white font-semibold py-3 text-sm sm:text-base rounded-lg transition-colors"
                  style={{ backgroundColor: '#FA2C37' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E02429'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FA2C37'}
                >
                  Proceed to Checkout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Global Modals from useMenuItemActions */}
      <ItemModifierModal
        isOpen={isModifierModalOpen}
        onClose={closeModifierModal}
        onAddToCart={editMode ? handleUpdateCartItem : handleAddToCartFromModal}
        initialModifiers={editMode ? selectedCartItem?.selected?.modifiers : undefined}
        initialQuantity={editMode ? selectedCartItem?.quantity : 1}
        editMode={editMode}
      />

      <RepeatCustomisationModal
        isOpen={isRepeatCustomisationModalOpen}
        itemName={currentMenuItem?.name || ''}
        modifiers={selectedCartItem?.selected?.modifiers || []}
        onRepeat={handleRepeatCustomisation}
        onChoose={handleChooseNewCustomisation}
        onClose={closeRepeatCustomisationModal}
      />

      <DeleteVariantModal
        isOpen={isDeleteVariantModalOpen}
        itemName={currentMenuItem?.name || ''}
        variants={deleteVariants}
        onUpdateQuantity={handleDeleteVariant}
        onClose={closeDeleteVariantModal}
      />
    </div>
  );
};

export default Cart;
